import { useMemo, useRef } from 'react';
import { z } from 'zod';
import type { FrontendTool } from '@erp/rpc';

export interface FrontendToolDefinition {
  name: string;
  description: string;
  schema: FrontendTool;
  /**
   * Runs the real client-side handler. For awaitable tools the return value is
   * POSTed back to the agent (see agent-state context); for fire-and-forget
   * tools it is ignored.
   */
  execute: (args: never) => unknown | Promise<unknown>;
  /** True when the agent should block on this tool's result (round-trip). */
  awaitable: boolean;
}

export function useFrontendTool<T extends z.ZodType>(config: {
  name: string;
  description: string;
  parameters: T;
  execute: (args: z.infer<T>) => unknown | Promise<unknown>;
  /**
   * When true, the backend blocks the agent until the frontend POSTs the
   * handler's return value back via submitFrontendToolResult, so the agent
   * sees the result in the same turn. Defaults to false (fire-and-forget).
   */
  awaitable?: boolean;
}): FrontendToolDefinition {
  const executeRef = useRef(config.execute);
  executeRef.current = config.execute;

  const awaitable = config.awaitable ?? false;

  return useMemo(
    () => ({
      name: config.name,
      description: config.description,
      awaitable,
      schema: {
        name: config.name,
        description: config.description,
        parameters: z.toJSONSchema(config.parameters) as Record<
          string,
          unknown
        >,
        awaitable,
      },
      execute: (args: z.infer<T>) => executeRef.current(args),
    }),
    [config.name, config.description, config.parameters, awaitable],
  );
}
